import { useState } from "react";
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/Reveal";
import { Section, Eyebrow, PageHero } from "@/components/common";
import { api } from "@/lib/api";
import { useContent } from "@/lib/useContent";
import { trackEvent } from "@/lib/analytics";

const socialIcon = { Facebook, Instagram, Twitter, YouTube: Youtube };
const TOPICS = ["General Question", "Volunteering", "Media / Press", "Events", "Endorsements", "Other"];

export default function Contact() {
  const { data: content } = useContent();
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      trackEvent("form_submit", { form: "contact" });
      toast.success("Thank you! Your message has been sent. We'll reply soon.");
      setForm({ name: "", email: "", phone: "", topic: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (!content) return <div className="h-screen" />;
  const c = content.contact || {};

  return (
    <div>
      <PageHero eyebrow="Contact" title="Get in touch" subtitle="Have a question, idea, or concern? Our team would love to hear from you." />

      <Section className="py-16">
        <div className="grid lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-3">
            <form onSubmit={submit} className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input required placeholder="Full Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
                <Input type="email" required placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
                <Input type="tel" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
                <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                  <SelectTrigger data-testid="contact-topic"><SelectValue placeholder="Topic" /></SelectTrigger>
                  <SelectContent>
                    {TOPICS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Textarea required placeholder="Your message *" rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
              <Button type="submit" disabled={busy} className="w-full bg-brightblue hover:bg-brightblue-hover text-white font-semibold h-11" data-testid="contact-submit">
                {busy ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2 space-y-5">
            <div className="hero-gradient text-white rounded-2xl p-7">
              <Eyebrow><span className="text-blue-300">Reach Us</span></Eyebrow>
              <ul className="space-y-4 mt-2">
                {c.email && (
                  <li className="flex items-start gap-3">
                    <Mail className="w-5 h-5 mt-0.5 text-blue-300 shrink-0" />
                    <a href={`mailto:${c.email}`} className="hover:text-blue-200 break-all" data-testid="contact-email-link">{c.email}</a>
                  </li>
                )}
                {c.email_alt && (
                  <li className="flex items-start gap-3">
                    <Mail className="w-5 h-5 mt-0.5 text-blue-300 shrink-0" />
                    <a href={`mailto:${c.email_alt}`} className="hover:text-blue-200 break-all" data-testid="contact-email-alt-link">{c.email_alt}</a>
                  </li>
                )}
                {c.phone && (
                  <li className="flex items-start gap-3">
                    <Phone className="w-5 h-5 mt-0.5 text-blue-300 shrink-0" />
                    <a href={`tel:${c.phone}`} className="hover:text-blue-200" data-testid="contact-phone-link">{c.phone}</a>
                  </li>
                )}
                {c.address && (
                  <li className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 mt-0.5 text-blue-300 shrink-0" />
                    <span>{c.address}</span>
                  </li>
                )}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-7 border border-slate-200 shadow-sm">
              <Eyebrow>Follow the Campaign</Eyebrow>
              <div className="flex gap-3 mt-2">
                {(c.socials || []).map((s) => {
                  const Icon = socialIcon[s.platform] || Mail;
                  return (
                    <a key={s.platform} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.platform}
                      className="w-11 h-11 rounded-full bg-sky text-navy hover:bg-brightblue hover:text-white flex items-center justify-center transition-colors"
                      data-testid={`contact-social-${s.platform.toLowerCase()}`}>
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
